import { SkyDialog } from "@skypointcloud/skypoint-ui";
import { useEffect, useRef, useState, type ReactElement } from "react";

/**
 * The unsaved-changes guard (round 3, owner 2026-09-30: "unless save changes is clicked it doesn't
 * get saved. so when user moves, it should show to save or discard"). The one leave guard in the
 * app: a screen that keeps an unsaved draft calls it with its `dirty` flag and its own save, and
 * renders the element it returns.
 *
 * While `when` holds:
 * - **In-app navigation** asks "Save changes?" with Save, Discard and Keep editing. wouter moves
 *   by `history.pushState` / `replaceState` (its `navigate`, read at call time), so the guard
 *   holds those two while it is armed: a call that changes the path is kept back until the reader
 *   answers; one that only changes the search or the hash of this page goes through. That covers
 *   every link, the nav drawer, the breadcrumb and a tab strip's `setLocation` alike. The browser's
 *   own Back button is not held (its `popstate` has already moved the address).
 * - **Closing or reloading the browser tab** gets the browser's own prompt (`beforeunload`).
 *
 * Save runs `onSave(proceed)`: the screen saves as its own Save does and calls `proceed` once the
 * save succeeded, which completes the navigation; a refused save leaves the reader on the screen
 * with its error beside the Save that failed. Discard completes the navigation and the screen's
 * draft goes with it. Keep editing, Escape or the close button stay.
 */
export function useLeaveGuard({
  when,
  onSave,
}: {
  when: boolean;
  onSave: (proceed: () => void) => void;
}): ReactElement {
  const [asking, setAsking] = useState(false);
  const pending = useRef<(() => void) | null>(null);
  const saveRef = useRef(onSave);
  useEffect(() => {
    saveRef.current = onSave;
  });

  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers read `returnValue`; the text is never shown, the browser has its own.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    const original = { push: history.pushState, replace: history.replaceState };
    const ask = (go: () => void) => {
      pending.current = go;
      setAsking(true);
    };
    history.pushState = holdPathChanges(original.push, ask);
    history.replaceState = holdPathChanges(original.replace, ask);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      history.pushState = original.push;
      history.replaceState = original.replace;
    };
  }, [when]);

  /** The held navigation, taken once: a second answer finds nothing to run. */
  function take(): (() => void) | null {
    const go = pending.current;
    pending.current = null;
    return go;
  }

  return (
    <SkyDialog
      open={asking}
      onOpenChange={(open) => {
        if (open) return;
        setAsking(false);
        pending.current = null;
      }}
      title="Save changes?"
      description="You have changes on this page that are not saved. Save them before you leave, or discard them."
      size="small"
      testId="leave-guard-dialog"
      tertiaryAction={{
        label: "Keep editing",
        onClick: () => {
          take();
        },
      }}
      secondaryAction={{ label: "Discard", onClick: () => take()?.() }}
      primaryAction={{
        label: "Save",
        onClick: () => {
          const go = take();
          saveRef.current(() => go?.());
        },
      }}
    />
  );
}

/**
 * `method` (the original `pushState` or `replaceState`) wrapped so a call that changes the path
 * is handed to `ask` as the navigation to run later; one that keeps the path runs now.
 */
function holdPathChanges(
  method: typeof history.pushState,
  ask: (go: () => void) => void,
): typeof history.pushState {
  return (data, unused, url) => {
    if (url === undefined || url === null || samePath(url)) {
      method.call(history, data, unused, url);
      return;
    }
    ask(() => method.call(history, data, unused, url));
  };
}

/** The URL names this page (only its search or hash may differ). */
function samePath(url: string | URL): boolean {
  try {
    return new URL(url, window.location.href).pathname === window.location.pathname;
  } catch {
    return false;
  }
}
